import React from "react";
import { ArrowRight, Award, Database, MailOpen, MousePointerClick } from "lucide-react";

/* Atomic atom — mid-century motif, nighttime palette */
const AtomMark = () => (
  <svg viewBox="0 0 280 280" className="w-full h-full" aria-hidden>
    <g style={{ transformOrigin: "50% 50%" }}>
      <ellipse cx="140" cy="140" rx="120" ry="44" fill="none" stroke="var(--bpm-cream)" strokeWidth="1" opacity="0.5" transform="rotate(0 140 140)" />
      <ellipse cx="140" cy="140" rx="120" ry="44" fill="none" stroke="var(--bpm-teal)" strokeWidth="1.2" opacity="0.9" transform="rotate(60 140 140)" />
      <ellipse cx="140" cy="140" rx="120" ry="44" fill="none" stroke="var(--bpm-coral)" strokeWidth="1.2" opacity="0.9" transform="rotate(-60 140 140)" />
    </g>
    {/* Starburst */}
    <g className="orbit" style={{ transformOrigin: "50% 50%" }}>
      {Array.from({ length: 24 }).map((_, i) => {
        const angle = (i * 360) / 24;
        return (
          <line
            key={i}
            x1="140" y1="22"
            x2="140" y2="38"
            stroke="var(--bpm-cream)"
            strokeWidth="1"
            opacity="0.5"
            transform={`rotate(${angle} 140 140)`}
          />
        );
      })}
    </g>
    {/* Nucleus */}
    <circle cx="140" cy="140" r="34" fill="var(--bpm-orange)" />
    <circle cx="140" cy="140" r="34" fill="none" stroke="var(--bpm-cream)" strokeWidth="1" />
    {/* Electrons */}
    <circle cx="260" cy="140" r="6" fill="var(--bpm-mustard)" />
    <circle cx="20" cy="140" r="4" fill="var(--bpm-teal)" />
  </svg>
);

const stats = [
  { icon: Award, k: "10+", v: "Years of Experience" },
  { icon: Database, k: "275,000+", v: "Largest List Handled" },
  { icon: MailOpen, k: "44% - 64%", v: "Opens Range" },
  { icon: MousePointerClick, k: "2% - 11%", v: "Clicks Range" },
];

export const Hero = () => {
  return (
    <section id="top" className="relative overflow-hidden grain" data-testid="hero-section">
      {/* Atmospheric gradient blobs */}
      <div
        className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full opacity-30 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, var(--bpm-mustard), transparent 65%)" }}
        aria-hidden
      />
      <div
        className="absolute -bottom-40 right-0 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, var(--bpm-teal), transparent 65%)" }}
        aria-hidden
      />

      <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 pt-8 md:pt-12 pb-8 md:pb-12 z-10">
        <div className="fade-up flex flex-col lg:flex-row items-start gap-8 lg:gap-12">
          {/* Atom — left of headline, sized to match the text block height */}
          <div className="relative w-[180px] h-[180px] md:w-[240px] md:h-[240px] lg:w-[340px] lg:h-[340px] shrink-0 self-start mt-1">
            <AtomMark />
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="font-display text-[42px] sm:text-6xl md:text-7xl lg:text-[88px]" data-testid="hero-headline">
              Email Marketing
              <br />
              <span style={{ color: "var(--bpm-orange)" }}>That Works</span>
            </h1>

            <p className="mt-8 max-w-xl text-[16px] md:text-[18px] leading-[1.65] text-[color:var(--bpm-cream-soft)]" data-testid="hero-subtext">
              We position your list to connect, grow, and flourish. Hands-on
              email management that addresses the issues holding your list
              back. Enterprise-level solutions for lists of every size — on
              budget, on time, on brand.
            </p>

            <div className="mt-10 flex items-center gap-4 flex-wrap">
              <a href="#contact" className="btn" data-testid="hero-cta-primary">
                Need Help? Let&apos;s Talk <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Callout stats — 6 boxes, single line */}
      <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 pb-12 md:pb-16 z-10">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hero-stats">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.v}
                className="callout fade-up p-4"
                style={{ animationDelay: `${120 + i * 60}ms` }}
                data-testid={`hero-stat-${i}`}
              >
                <div className="flex items-center mb-3">
                  <Icon size={16} style={{ color: "var(--bpm-teal)" }} />
                </div>
                <div
                  className="font-display text-[18px] md:text-[20px] leading-[1.1]"
                  style={{ color: "var(--bpm-cream)" }}
                >
                  {s.k}
                </div>
                <div
                  className="mt-1.5 text-[10px] uppercase text-[color:var(--bpm-cream-soft)]"
                  style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500, letterSpacing: "0.06em" }}
                >
                  {s.v}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Hero;
